import dayjs from "dayjs";
import {
  GroupAppointment,
  GroupFilterChoice,
  GroupFilterKey,
  GroupFilterOptions,
  GroupFilterSection,
  GroupFilterState,
} from "./group-appointment-types";
import {
  FORM_STATUS,
  GROUP_APPOINTMENT_DURATION_SUFFIX,
  GROUP_APPOINTMENT_STATUS,
  GROUP_APPOINTMENT_TIME_FORMAT,
  GROUP_FILTER_KEY,
  GROUP_STATUS_FILTER_OPTIONS,
  INDIVIDUAL_TO_GROUP_STATUS,
} from "./group-appointment-constant";

/** "4:00 PM | 15 Mins" for the Time & Duration column. */
export const formatTimeAndDuration = (
  startTime: string,
  duration: number | null
) => {
  const time = dayjs(startTime).format(GROUP_APPOINTMENT_TIME_FORMAT);
  return duration === null
    ? time
    : `${time} | ${duration} ${GROUP_APPOINTMENT_DURATION_SUFFIX}`;
};

export const getFormStatusVariant = (status?: string) =>
  status === FORM_STATUS.SUBMITTED
    ? ("success" as const)
    : ("warning" as const);

export const getAppointmentStatusVariant = (status?: string) => {
  if (status === GROUP_APPOINTMENT_STATUS.COMPLETED) return "default" as const;
  if (status === GROUP_APPOINTMENT_STATUS.CANCELLED) return "error" as const;
  if (status === GROUP_APPOINTMENT_STATUS.DOC_PENDING)
    return "warning" as const;
  return "success" as const;
};

/** A group session as the scheduling calendar expects an event. */
export type GroupSessionEvent = GroupAppointment & {
  title: string;
  start: Date;
  end: Date | null;
  patientName: string;
  status: string;
  isGroupSession: true;
};

type EventDates = {
  toStart: (startTime: string) => string;
  toEnd: (endTime: string) => string;
  /** End of the session's day, for the month view's all-day rows. */
  toDayEnd: (startTime: string) => Date;
  isMonthView: boolean;
};

/**
 * Group sessions as calendar events. The event cards read `title`,
 * `patientName`, `status` and `mode`, so a session fills those with the group
 * it belongs to.
 *
 * @author Rajkumar Rathod
 */
export const toGroupSessionEvents = (
  sessions: GroupAppointment[],
  { toStart, toEnd, toDayEnd, isMonthView }: EventDates
): GroupSessionEvent[] =>
  sessions.map((session) => ({
    ...session,
    title: session.groupName,
    patientName: session.groupName,
    status: session.sessionStatus,
    start: new Date(toStart(session.startTime)),
    end: session.endTime
      ? new Date(toEnd(session.endTime))
      : isMonthView
        ? toDayEnd(session.startTime)
        : null,
    isGroupSession: true,
  }));

const toFilterSection = (
  choices: GroupFilterChoice[],
  { selected, isExpand }: GroupFilterState[GroupFilterKey]
): GroupFilterSection => {
  const optList = choices.map((choice) => ({
    ...choice,
    selected: selected.includes(choice.value),
  }));
  const activeFilterCount = optList.filter((opt) => opt.selected).length;

  return {
    optList,
    isExpand,
    isSelectAll: !!optList.length && activeFilterCount === optList.length,
    activeFilterCount,
  };
};

/** Location and status checkbox sections for SideFilters. */
export const toGroupFilterOptions = (
  state: GroupFilterState,
  locationChoices: GroupFilterChoice[]
): GroupFilterOptions => ({
  [GROUP_FILTER_KEY.LOCATIONS]: toFilterSection(
    locationChoices,
    state[GROUP_FILTER_KEY.LOCATIONS]
  ),
  [GROUP_FILTER_KEY.STATUS]: toFilterSection(
    GROUP_STATUS_FILTER_OPTIONS,
    state[GROUP_FILTER_KEY.STATUS]
  ),
});

const toSectionState = ({
  optList,
  isExpand,
}: GroupFilterSection): GroupFilterState[GroupFilterKey] => ({
  selected: optList.filter((opt) => opt.selected).map((opt) => opt.value),
  isExpand,
});

export const toGroupFilterState = (
  options: GroupFilterOptions
): GroupFilterState => ({
  [GROUP_FILTER_KEY.LOCATIONS]: toSectionState(
    options[GROUP_FILTER_KEY.LOCATIONS]
  ),
  [GROUP_FILTER_KEY.STATUS]: toSectionState(options[GROUP_FILTER_KEY.STATUS]),
});

/** Ticked individual statuses as the session statuses the API filters on. */
export const toGroupSessionStatuses = (selected: string[]): string[] => [
  ...new Set(
    selected.map((value) => INDIVIDUAL_TO_GROUP_STATUS[value] ?? value)
  ),
];
